import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_SCRIPT_ID = 'google-identity-services';

const loadGoogleScript = () =>
  new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_SCRIPT_ID);
    if (existingScript) {
      if (window.google?.accounts?.id) {
        resolve();
      } else {
        existingScript.addEventListener('load', resolve, { once: true });
        existingScript.addEventListener('error', reject, { once: true });
      }
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_SCRIPT_ID;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

const GoogleAuthButton = ({ mode = 'signin' }) => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const buttonRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [isUnavailable, setIsUnavailable] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const initializeGoogle = async () => {
      if (!GOOGLE_CLIENT_ID) {
        setIsUnavailable(true);
        return;
      }

      try {
        await loadGoogleScript();
        if (!isMounted || !window.google?.accounts?.id || !buttonRef.current) {
          return;
        }

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (response) => {
            if (!response.credential) {
              toast.error('Google sign-in did not return a credential.');
              return;
            }

            try {
              const { data } = await axios.post(
                `${BACKEND_URL}/api/auth/google`,
                { credential: response.credential },
                { withCredentials: true }
              );
              setUser(data);
              toast.success(mode === 'signin' ? 'Login successful!' : 'Registration successful!');
              navigate('/products', { replace: true });
            } catch (error) {
              toast.error(formatApiErrorDetail(error.response?.data?.detail));
            }
          }
        });

        buttonRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: 'outline',
          size: 'large',
          text: mode === 'signin' ? 'signin_with' : 'signup_with',
          shape: 'rectangular',
          width: Math.max(buttonRef.current.offsetWidth || 320, 280),
          logo_alignment: 'left'
        });

        if (isMounted) {
          setIsReady(true);
        }
      } catch (error) {
        console.error('Failed to initialize Google sign-in:', error);
        if (isMounted) {
          setIsUnavailable(true);
        }
      }
    };

    initializeGoogle();

    return () => {
      isMounted = false;
    };
  }, [mode, navigate, setUser]);

  if (isUnavailable) {
    return (
      <div className="mt-4 rounded-sm border border-[#E6E2D6] bg-[#F8F5EE] px-4 py-3 text-center text-sm text-[#7A6B62]">
        Google sign-in is unavailable until `REACT_APP_GOOGLE_CLIENT_ID` is configured for Panchami.
      </div>
    );
  }

  return (
    <div className="mt-4 w-full">
      {!isReady && (
        <div className="flex h-11 w-full items-center justify-center rounded-sm border border-[#E6E2D6] bg-white text-sm text-[#5C5C5C]">
          Loading Google sign-in...
        </div>
      )}
      <div
        ref={buttonRef}
        className={isReady ? 'w-full' : 'hidden'}
        data-testid={`google-${mode}-button`}
      />
    </div>
  );
};

export default GoogleAuthButton;
